"use client";

import {
  Checkbox,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { TreeLeaf } from "@/lib/tool-kit/stats-distributions-more/probability-tree";
import type { Rational } from "@/lib/tool-kit/stats-distributions-more/rational";

export interface TreeFieldLabels {
  outlineLabel: string;
  outlineHint: string;
  outcomesLabel: string;
  outcomesHint: string;
  stagesLabel: string;
  bagLabel: string;
  bagHint: string;
  drawsLabel: string;
  replaceLabel: string;
  outcomeColumn: string;
  calculationColumn: string;
  probabilityColumn: string;
  includeColumn: string;
  include: string;
  clearSelection: string;
}

export function OutlineField({
  value,
  onChange,
  labels,
}: {
  value: string;
  onChange: (value: string) => void;
  labels: TreeFieldLabels;
}) {
  return (
    <TextAreaField
      hint={labels.outlineHint}
      id="pt-outline"
      label={labels.outlineLabel}
      onChange={onChange}
      rows={8}
      value={value}
    />
  );
}

export function StagesFields({
  outcomes,
  stages,
  onOutcomes,
  onStages,
  labels,
}: {
  outcomes: string;
  stages: string;
  onOutcomes: (value: string) => void;
  onStages: (value: string) => void;
  labels: TreeFieldLabels;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
      <TextAreaField
        hint={labels.outcomesHint}
        id="pt-outcomes"
        label={labels.outcomesLabel}
        onChange={onOutcomes}
        rows={4}
        value={outcomes}
      />
      <TextField
        id="pt-stages"
        label={labels.stagesLabel}
        numeric
        onChange={onStages}
        value={stages}
      />
    </div>
  );
}

export function BagFields({
  items,
  draws,
  replace,
  onItems,
  onDraws,
  onReplace,
  labels,
}: {
  items: string;
  draws: string;
  replace: boolean;
  onItems: (value: string) => void;
  onDraws: (value: string) => void;
  onReplace: (value: boolean) => void;
  labels: TreeFieldLabels;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
      <TextAreaField
        hint={labels.bagHint}
        id="pt-bag"
        label={labels.bagLabel}
        onChange={onItems}
        rows={4}
        value={items}
      />
      <div className="space-y-4">
        <TextField
          id="pt-draws"
          label={labels.drawsLabel}
          numeric
          onChange={onDraws}
          value={draws}
        />
        <Checkbox
          checked={replace}
          label={labels.replaceLabel}
          onChange={onReplace}
        />
      </div>
    </div>
  );
}

/** The outcomes with a tick-box each, to build an event and add its probabilities. */
export function OutcomeTable({
  leaves,
  selected,
  onToggle,
  onClear,
  text,
  labels,
}: {
  leaves: readonly TreeLeaf[];
  selected: ReadonlySet<string>;
  onToggle: (id: string, on: boolean) => void;
  onClear: () => void;
  text: {
    branch: (probability: Rational) => string;
    product: (leaf: TreeLeaf) => string;
    path: (leaf: TreeLeaf) => string;
  };
  labels: TreeFieldLabels;
}) {
  return (
    <div className="space-y-2">
      <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead className="sticky top-0 bg-card">
            <tr>
              <th className={TH} scope="col">
                {labels.includeColumn}
              </th>
              <th className={TH} scope="col">
                {labels.outcomeColumn}
              </th>
              <th className={TH} scope="col">
                {labels.calculationColumn}
              </th>
              <th className={`${TH} text-right`} scope="col">
                {labels.probabilityColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((leaf) => (
              <tr
                className={selected.has(leaf.id) ? "bg-primary/10" : undefined}
                key={leaf.id}
              >
                <td className={TD}>
                  <input
                    aria-label={`${labels.include}: ${text.path(leaf)}`}
                    checked={selected.has(leaf.id)}
                    className="accent-primary"
                    onChange={(event) =>
                      onToggle(leaf.id, event.target.checked)
                    }
                    type="checkbox"
                  />
                </td>
                <th className={`${TD} font-medium`} scope="row">
                  {text.path(leaf)}
                </th>
                <td className={`${TD} font-mono text-muted-foreground`}>
                  {leaf.branches.map(text.branch).join(" × ")}
                </td>
                <td className={`${TD} text-right font-mono`}>
                  {text.product(leaf)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className={BUTTON_SMALL} onClick={onClear} type="button">
        {labels.clearSelection}
      </button>
    </div>
  );
}
